import { StravaConnectButton } from "./StravaConnectButton";

interface ConnectStravaPromptProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function ConnectStravaPrompt({
  isOpen,
  onClose,
  onSuccess,
}: ConnectStravaPromptProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="relative flex h-auto min-h-screen w-full flex-col bg-background-light dark:bg-background-dark font-display">
        <div className="flex flex-col h-full grow p-4">
          {/* Strava Logo */}
          <div className="flex w-full justify-center items-center pt-16 pb-8">
            <img
              src="/stravaWhite.png"
              alt="Strava"
              className="h-12 w-auto object-contain"
            />
          </div>

          {/* Headline Text */}
          <h1 className="text-gray-900 dark:text-white tracking-tight text-3xl font-bold leading-tight px-4 text-center pb-2 pt-6">
            Almost there! Connect your Strava.
          </h1>

          {/* Body Text */}
          <p className="text-gray-600 dark:text-gray-400 text-base font-normal leading-normal pb-8 pt-1 px-4 text-center">
            Connect Strava to automatically track and verify your challenge
            progress.
          </p>

          {/* Benefits List */}
          <div className="flex flex-col gap-2 px-4 pb-8">
            {/* List Item 1: Automatic sync */}
            <div className="flex items-center gap-4 bg-transparent p-2">
              <div className="text-gray-900 dark:text-white flex items-center justify-center rounded-lg bg-primary/20 shrink-0 size-12">
                <span className="material-symbols-outlined text-primary text-2xl">
                  autorenew
                </span>
              </div>
              <div className="flex flex-col justify-center">
                <p className="text-gray-900 dark:text-white text-base font-medium leading-normal line-clamp-1">
                  Automatic sync
                </p>
                <p className="text-gray-600 dark:text-gray-400 text-sm font-normal leading-normal line-clamp-2">
                  Your activities are logged for you.
                </p>
              </div>
            </div>

            {/* List Item 2: Fair play */}
            <div className="flex items-center gap-4 bg-transparent p-2">
              <div className="text-gray-900 dark:text-white flex items-center justify-center rounded-lg bg-primary/20 shrink-0 size-12">
                <span className="material-symbols-outlined text-primary text-2xl">
                  shield
                </span>
              </div>
              <div className="flex flex-col justify-center">
                <p className="text-gray-900 dark:text-white text-base font-medium leading-normal line-clamp-1">
                  Fair play
                </p>
                <p className="text-gray-600 dark:text-gray-400 text-sm font-normal leading-normal line-clamp-2">
                  Ensures all challenge participants are verified.
                </p>
              </div>
            </div>

            {/* List Item 3: Stay focused */}
            <div className="flex items-center gap-4 bg-transparent p-2">
              <div className="text-gray-900 dark:text-white flex items-center justify-center rounded-lg bg-primary/20 shrink-0 size-12">
                <span className="material-symbols-outlined text-primary text-2xl">
                  directions_run
                </span>
              </div>
              <div className="flex flex-col justify-center">
                <p className="text-gray-900 dark:text-white text-base font-medium leading-normal line-clamp-1">
                  Stay focused
                </p>
                <p className="text-gray-600 dark:text-gray-400 text-sm font-normal leading-normal line-clamp-2">
                  Concentrate on your workout, not manual entries.
                </p>
              </div>
            </div>
          </div>

          {/* Spacer */}
          <div className="flex-grow"></div>

          {/* Security Note */}
          <p className="text-gray-500 dark:text-gray-500 text-xs font-normal leading-normal px-4 text-center pb-4">
            We'll only have permission to view your activities.
          </p>

          {/* Use existing StravaConnectButton */}
          <div className="px-4 pb-2">
            <StravaConnectButton onSuccess={onSuccess} />
          </div>

          {/* Dismiss Link */}
          <div className="px-4 pt-2 pb-6">
            <button
              onClick={onClose}
              className="flex items-center justify-center h-10 w-full rounded-full px-4 text-gray-600 dark:text-gray-400 text-base font-medium leading-normal hover:bg-white/10 active:bg-white/20"
            >
              I'll do this later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
